import { useLazyQuery } from '@apollo/client';
import { User } from 'mission-control-common-components';
import { useCallback, useMemo, useRef } from 'react';

import type { TraffickingFilters } from '../../../../../apis/graphql/types';
import type { DropdownOption, DropdownOptionGroup } from '../../../../../common/Dropdown/Dropdown';
import useDebounceFilter from '../../../../../common/hooks/useDebounceFilter';
import { useFiltersContext } from '../../../../../contexts/FilterContext';
import type { Nullable } from '../../../../../models/utils';
import { normalizeFilterPayloadKeys } from '../normalizeFilterPayloadKeys';
import { GET_TRAFFICKING_CUSTOM_FILTER } from '../queries/traffickingCustomFilter';
import type {
  TraffickingCustomFilterQueryResponse,
  TraffickingCustomFilterQueryVariables,
  TraffickingCustomFilterSummary,
} from '../types';
import { useTraffickingCustomFilters } from './useTraffickingCustomFilters';

type UseCustomSavedFiltersResult = {
  groups: DropdownOptionGroup<TraffickingCustomFilterSummary>[];
  isLoading: boolean;
  error: Error | undefined;
  searchValue: string;
  onSearchChange: (term: string) => void;
  onSearchClear: () => void;
  selectedFilter: Nullable<DropdownOption<TraffickingFilters>>;
  selectedFilterSummary: TraffickingCustomFilterSummary | null;
  onSelectFilter: (option: DropdownOption<TraffickingCustomFilterSummary>) => void;
  onClearFilter: () => void;
};

export function useCustomSavedFilters(): UseCustomSavedFiltersResult {
  const { selectedCustomFilter, applyCustomFilter } = useFiltersContext();
  const { filterValue, debouncedFilterValue, onChange, onClear } = useDebounceFilter();

  const { filters: allFilters, groups, loading: listLoading, error: listError } = useTraffickingCustomFilters(
    debouncedFilterValue
  );

  const lastSelectedSummaryRef = useRef<TraffickingCustomFilterSummary | null>(null);

  const [fetchFullFilter, { loading: detailLoading }] = useLazyQuery<
    TraffickingCustomFilterQueryResponse,
    TraffickingCustomFilterQueryVariables
  >(GET_TRAFFICKING_CUSTOM_FILTER, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const raw = data.traffickingCustomFilter;
      if (!raw) return;
      const currentUser = User.getUserInfo().userInfo?.email ?? '';
      const option: DropdownOption<TraffickingFilters> = {
        id: raw.id,
        label: raw.filterName,
        value: normalizeFilterPayloadKeys(raw.filterPayload),
      };
      lastSelectedSummaryRef.current = {
        id: raw.id,
        name: raw.filterName,
        visibility: raw.visibility as TraffickingCustomFilterSummary['visibility'],
        createdByMe: (raw.createdBy ?? '').toLowerCase() === currentUser.toLowerCase(),
        type: raw.filterType as TraffickingCustomFilterSummary['type'],
      };
      applyCustomFilter(option);
    },
  });

  const onSelectFilter = useCallback(
    (option: DropdownOption<TraffickingCustomFilterSummary>) => {
      // Toggle off if re-selecting the same filter
      if (selectedCustomFilter?.id === option.id) {
        applyCustomFilter(null);
        lastSelectedSummaryRef.current = null;
        return;
      }
      // Store the summary from the option value immediately
      if (option.value) {
        lastSelectedSummaryRef.current = option.value;
      }
      if (!option.id) return;
      fetchFullFilter({ variables: { id: option.id } });
    },
    [selectedCustomFilter, applyCustomFilter, fetchFullFilter]
  );

  const onClearFilter = useCallback(() => {
    applyCustomFilter(null);
    lastSelectedSummaryRef.current = null;
  }, [applyCustomFilter]);

  // Derive selectedFilterSummary: prefer allFilters (canonical server data, fresh
  // after refetch on mutation), fall back to the ref (holds data before the list
  // updates, e.g. right after selection).
  const selectedFilterSummary = useMemo((): TraffickingCustomFilterSummary | null => {
    if (!selectedCustomFilter) return null;
    const fromList = allFilters.find((f) => f.id === selectedCustomFilter.id);
    if (fromList) {
      lastSelectedSummaryRef.current = fromList;
      return fromList;
    }
    if (lastSelectedSummaryRef.current?.id === selectedCustomFilter.id) {
      return lastSelectedSummaryRef.current;
    }
    return null;
  }, [selectedCustomFilter, allFilters]);

  return {
    groups,
    isLoading: listLoading || detailLoading,
    error: listError,
    searchValue: filterValue,
    onSearchChange: onChange,
    onSearchClear: onClear,
    selectedFilter: selectedCustomFilter,
    selectedFilterSummary,
    onSelectFilter,
    onClearFilter,
  };
}
